/**
 * Customer-success agent — the producer that keeps us on top of every live
 * customer, not just the noisy ones.
 *
 * Where `revenueAgent` protects the money already on the table (overdue
 * invoices, renewals), this protects the *relationship*: it scores every live
 * customer's health from their real record (see `crm/health.ts`) and, for each
 * account that has slipped into the watch / at-risk bands, proposes a *real*
 * `create_task` check-in into the approvals inbox so a human reaches out before
 * the account quietly churns.
 *
 * It performs no write itself — it only proposes. The operator approves in the
 * Dashboard approvals inbox, and `resolveApproval` runs the action through
 * `runActionWithUndo`, which genuinely creates the task (tied to the customer
 * so it shows on their page) and is undoable. So the loop is honest end-to-end:
 *
 *   real health signals  →  real proposal  →  operator approval  →  real write
 *
 * The check-in task carries the account's ARR in `value` — the dollars the
 * relationship is worth, i.e. what's at risk if the account churns. Influenced
 * / at-risk, never banked. Omitted when the account has no value set, so the
 * value ledger never invents a number.
 */

import { rankCustomersByRisk, type HealthBand } from '../crm/health';
import { queueApproval, listApprovals } from './approvals';
import type { Person, ProjectTask } from '../db/types';
import type { CreateTaskAction } from './types';

/**
 * Only accounts at or below this band get a proposed check-in. Healthy
 * accounts don't need a nudge; watch + at-risk do. Tuned conservatively so the
 * inbox stays signal, not noise.
 */
const BANDS_NEEDING_CHECKIN: ReadonlySet<HealthBand> = new Set(['watch', 'at-risk']);

export interface ProposeHealthCheckinsResult {
  /** How many new check-in tasks were queued for approval. */
  proposed: number;
  /** Slipping accounts that already had a matching pending task (skipped). */
  skipped: number;
}

/**
 * Deterministic title for a customer's check-in task. Stable per customer so
 * re-running the agent de-dupes against a pending proposal for the same
 * account rather than piling duplicates.
 */
export function checkinTitleFor(person: Person): string {
  return `Check in — ${person.name}`;
}

/**
 * Build the concrete `create_task` proposal for a slipping account. The task is
 * tied to the customer (`personId`) so it surfaces on their page, carries the
 * ARR at risk in `value`, and names the band + the single biggest risk factor
 * in its title's detail so the operator sees *why* at a glance.
 *
 * Exported for unit testing the priority + value derivation without the store.
 */
export function checkinTaskFor(person: Person, band: HealthBand): CreateTaskAction {
  return {
    kind: 'create_task',
    personId: person.id,
    personName: person.name,
    title: checkinTitleFor(person),
    // At-risk accounts are urgent; watch accounts are a high-priority nudge.
    priority: band === 'at-risk' ? 'urgent' : 'high',
    dueDate: null,
    // The ARR the relationship protects — undefined when unset (never invented).
    value: person.accountValue,
  };
}

/**
 * Score every live customer and queue a `create_task` check-in approval for
 * each one in the watch / at-risk bands. De-dupes by task title against pending
 * approvals so re-running doesn't pile duplicate check-ins for the same
 * account. Returns how many were proposed vs. skipped.
 */
export async function proposeHealthCheckins(
  people: Person[],
  tasks: ProjectTask[] = [],
  now = Date.now(),
): Promise<ProposeHealthCheckinsResult> {
  const ranked = rankCustomersByRisk(people, { tasks, now });
  const slipping = ranked.filter((r) => BANDS_NEEDING_CHECKIN.has(r.health.band));

  // Don't propose a second check-in for an account that already has one waiting.
  const pending = await listApprovals('pending');
  const alreadyQueued = new Set(
    pending
      .filter((a) => a.action.kind === 'create_task')
      .map((a) => (a.action as CreateTaskAction).title),
  );

  let proposed = 0;
  let skipped = 0;
  for (const { person, health } of slipping) {
    const action = checkinTaskFor(person, health.band);
    if (alreadyQueued.has(action.title)) {
      skipped++;
      continue;
    }
    // Name the band + top risk factor in the note so the inbox explains itself.
    const topFactor = health.factors.find((f) => f.points < 0);
    const detail = `Health ${health.score}/100 (${health.band})${
      topFactor ? ` · ${topFactor.label}` : ''
    }`;
    await queueApproval(action, 'scheduler', detail);
    alreadyQueued.add(action.title);
    proposed++;
  }

  return { proposed, skipped };
}
